import { Typography as MuiTypography } from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { z } from "zod";

import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import React, { useMemo } from "react";
import DepthHeatmapChartContainer from "../../components/charts/depthHeatmapChart/DepthHeatmapChartContainer";
import useDropdown from "../../hooks/useDropdown";
import { usePostHeight } from "../../hooks/usePostHeight";

const Typography = styled(MuiTypography)(spacing);

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;

const TODAY = new Date();
export function getWaterYearRange(date = TODAY) {
  const year = date.getFullYear();
  const startYear = year - 2;
  return {
    start_date: `${startYear}-10-01`,
    end_date: `${year}-09-30`,
  };
}
const { start_date, end_date } = getWaterYearRange();

const LOCATION_OPTIONS = [
  { label: "CCR1 – Cherry Creek Res. Station 1", value: "79" },
  { label: "CCR2 – Cherry Creek Res. Station 2", value: "80" },
  { label: "CCR3 – Cherry Creek Res. Station 3", value: "81" },
  { label: "CCR Composite – Cherry Creek Res.", value: "120" },
];

const DEFAULT_SEARCH_PARAMS = {
  start_date,
  end_date,
  locationid: 80,
  parameterid: 29,
};

const searchParamsSchema = z.object({
  start_date: z.string().default(DEFAULT_SEARCH_PARAMS.start_date),
  end_date: z.string().default(DEFAULT_SEARCH_PARAMS.end_date),
  locationid: z.coerce.number().default(DEFAULT_SEARCH_PARAMS.locationid),
  parameterid: z.coerce.number().default(DEFAULT_SEARCH_PARAMS.parameterid),
  controls: z.preprocess(
    (val) => val === "true" || val === true,
    z.boolean().default(false)
  ),
  height: z.preprocess(
    (val) => (typeof val === "string" ? parseInt(val, 10) : val),
    z.number().default(400)
  ),
});

const ENDPOINT = "depth-profile-graph-data";

export default function FieldDepthProfileEmbed() {
  usePostHeight();
  const { params, setParams } = useQueryParams(searchParamsSchema);

  const parOptions = useDropdown(
    `${ENDPOINT}/list-parameters-dropdown-depth-profile-active`
  );

  const getParameterLabel = () => {
    const param = parOptions.find(
      (p) => p.value === String(params.parameterid)
    );
    return param ? param.label : "Water Quality Parameter";
  };

  const getLocationLabel = () => {
    const location = LOCATION_OPTIONS.find(
      (l) => l.value === String(params.locationid)
    );
    return location ? location.label : "Location";
  };

  const FILTERS_CONFIG = useMemo(
    () => [
      {
        type: "date",
        param: "start_date",
        label: "Start Date",
        default: params.start_date,
      },
      {
        type: "date",
        param: "end_date",
        label: "End Date",
        default: params.end_date,
      },
      {
        type: "select",
        param: "locationid",
        label: "Surface-water location",
        default: String(params.locationid),
        options: LOCATION_OPTIONS,
      },
      {
        type: "auto",
        param: "parameterid",
        label: "Parameter",
        default: String(params.parameterid),
        options: parOptions.filter(({ value }) =>
          [
            20, 634, 638, 25, 24, 2, 42, 10, 6, 29, 45, 50, 1, 27, 16, 11, 3,
            680, 1892, 28, 46, 47, 652, 19, 30, 100139, 100140, 100138, 8, 53,
            9, 7, 1902,
          ].includes(+value)
        ),
      },
    ],
    [parOptions, params]
  );

  return (
    <>
      <main>
        {params.controls && (
          <Section>
            <Typography variant="h5" component="h2" gutterBottom>
              Filters
            </Typography>
            <FiltersForm config={FILTERS_CONFIG} onChange={setParams} />
          </Section>
        )}

        <Section>
          <DepthHeatmapChartContainer
            start_date={params.start_date}
            end_date={params.end_date}
            locationid={params.locationid}
            parameterid={params.parameterid}
            height={params.height}
            title={`${getParameterLabel()} at ${getLocationLabel()}`}
          />
        </Section>
      </main>
    </>
  );
}
